import { Chart, Plugin } from "chart.js";

interface StackedBarTopRadiusPluginOptions {
  radius?: number;
  roundBothEnds?: boolean;
}

// Runtime bar element shape from Chart.js (not fully typed)
interface BarElementRuntime {
  x: number;
  y: number;
  base: number;
  width: number;
}

export const stackedBarTopRadiusPlugin: Plugin<
  "bar",
  StackedBarTopRadiusPluginOptions
> = {
  id: "stackedBarTopRadius",

  afterDatasetsDraw(chart: Chart<"bar">, _args, pluginOptions) {
    const { ctx } = chart;

    const radius = pluginOptions?.radius ?? 4;
    const roundBothEnds = pluginOptions?.roundBothEnds ?? false;

    const metas = chart.getSortedVisibleDatasetMetas();

    // Group bars by index (x position)
    const barsByIndex = new Map<number, BarElementRuntime[]>();

    for (const meta of metas) {
      if (meta.type !== "bar") {
        continue;
      }

      for (const [index, element] of meta.data.entries()) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        const bar = element as unknown as BarElementRuntime;
        // Skip bars with no height (null/0 values)
        const barHeight = Math.abs(bar.base - bar.y);
        if (barHeight <= 0) {
          continue;
        }

        if (!barsByIndex.has(index)) {
          barsByIndex.set(index, []);
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        barsByIndex.get(index)!.push(bar);
      }
    }

    ctx.save();

    for (const bars of barsByIndex.values()) {
      if (bars.length === 0) {
        continue;
      }

      // Get first bar for x and width (all bars in stack share these)
      const firstBar = bars[0];
      if (!firstBar) {
        continue;
      }

      const { x, width } = firstBar;

      // Find the actual visual top and bottom of the entire stack
      // For negative values, the visual top could be at base (baseline), not y
      const allYValues = bars.flatMap((b) => [b.y, b.base]);
      const topY = Math.min(...allYValues);
      const bottomY = Math.max(...allYValues);
      const totalStackHeight = Math.abs(bottomY - topY);

      if (totalStackHeight <= 0) {
        continue;
      }

      const actualRadius = Math.min(radius, totalStackHeight, width / 2);

      const left = x - width / 2;
      const right = x + width / 2;

      // Cut out just the corner regions that should be transparent
      // (the square corner minus the quarter circle)
      // Use a small padding to avoid anti-aliasing artifacts at edges
      const padding = 0.5;

      ctx.save();
      ctx.globalCompositeOperation = "destination-out";

      // Top-left: draw the area outside the curve (square minus arc)
      ctx.beginPath();
      ctx.moveTo(left - padding, topY - padding);
      ctx.lineTo(left + actualRadius, topY - padding);
      ctx.lineTo(left + actualRadius, topY);
      ctx.arc(
        left + actualRadius,
        topY + actualRadius,
        actualRadius,
        -Math.PI / 2,
        Math.PI,
        true,
      );
      ctx.lineTo(left - padding, topY + actualRadius);
      ctx.closePath();
      ctx.fill();

      // Top-right: draw the area outside the curve
      ctx.beginPath();
      ctx.moveTo(right + padding, topY - padding);
      ctx.lineTo(right - actualRadius, topY - padding);
      ctx.lineTo(right - actualRadius, topY);
      ctx.arc(
        right - actualRadius,
        topY + actualRadius,
        actualRadius,
        -Math.PI / 2,
        0,
        false,
      );
      ctx.lineTo(right + padding, topY + actualRadius);
      ctx.closePath();
      ctx.fill();

      // Round bottom corners if enabled
      if (roundBothEnds) {
        const bottomRadius = Math.min(radius, totalStackHeight, width / 2);

        // Bottom-left: draw the area outside the curve
        ctx.beginPath();
        ctx.moveTo(left - padding, bottomY + padding);
        ctx.lineTo(left + bottomRadius, bottomY + padding);
        ctx.lineTo(left + bottomRadius, bottomY);
        ctx.arc(
          left + bottomRadius,
          bottomY - bottomRadius,
          bottomRadius,
          Math.PI / 2,
          Math.PI,
          false,
        );
        ctx.lineTo(left - padding, bottomY - bottomRadius);
        ctx.closePath();
        ctx.fill();

        // Bottom-right: draw the area outside the curve
        ctx.beginPath();
        ctx.moveTo(right + padding, bottomY + padding);
        ctx.lineTo(right - bottomRadius, bottomY + padding);
        ctx.lineTo(right - bottomRadius, bottomY);
        ctx.arc(
          right - bottomRadius,
          bottomY - bottomRadius,
          bottomRadius,
          Math.PI / 2,
          0,
          true,
        );
        ctx.lineTo(right + padding, bottomY - bottomRadius);
        ctx.closePath();
        ctx.fill();
      }

      ctx.restore();
    }

    ctx.restore();
  },
};
